<template>
  <div
    v-if="showBanner"
    ref="bannerRef"
    :data-testid="dataTestid"
    data-smart-app-banner
    class="sm:hidden fixed top-0 left-0 right-0 z-[2147483001] bg-semantic-surface-container-lowest border-b border-semantic-outline-variant p-4 flex items-center gap-3 shadow-elevation-1"
  >
    <button
      class="text-semantic-surface-on-surface-variant text-lg leading-none shrink-0"
      aria-label="Dismiss"
      @click="dismiss"
    >
      ✕
    </button>
    <img
      src="/cc-icon.png"
      alt="Compare Club"
      width="40"
      height="40"
      class="rounded-lg shrink-0"
    />
    <div class="flex-1 min-w-0">
      <p class="text-sm font-semibold text-semantic-surface-on-surface truncate">Compare Club App</p>
      <p class="text-xs text-semantic-surface-on-surface-variant truncate">Manage your bills on the go</p>
    </div>
    <v2-atoms-button
      id="smart-app-banner-get"
      size="small"
      theme="secondary"
      :to="storeUrl"
      target="_blank"
      rel="noopener"
      class="shrink-0"
    >
      Get
    </v2-atoms-button>
  </div>
</template>

<script setup>
/*
 * Custom Smart App Banner for Android (all browsers) and iOS (non-Safari browsers).
 * Safari iOS uses the native <meta name="apple-itunes-app"> tag instead.
 *
 * - Only shows on mobile (hidden via sm:hidden)
 * - Only shows on /dashboard/* routes
 * - Persists dismiss in localStorage for 14 days
 * - Publishes its height as the --smart-app-banner-height root CSS var
 */
import memberhubConstants from '~/constants/memberhub.constants'
import { Capacitor } from '@capacitor/core'

defineOptions({ name: 'V2AtomsSmartAppBanner' })

defineProps({
  dataTestid: { type: String, default: 'smart-app-banner' }
})

const DISMISS_KEY = 'smart-app-banner:dismissed-at'
const DISMISS_DAYS = 14

const route = useRoute()
const showBanner = ref(false)
const bannerRef = ref(null)

defineExpose({ showBanner })

// ── Layout offset ─────────────────────────────────────────────────────────
// The banner is fixed at top-0, so everything else anchored to the top of the
// dashboard (header, sidebar drawer, notification slider, full-page modals) has
// to start below it. Publish the measured height as a root CSS custom property:
// consumers offset themselves in plain CSS with no Vue wiring or event handling,
// and it always resolves to 0px when the banner is absent, dismissed or hidden
// at >= sm — including when the viewport crosses that breakpoint while they are open.
const HEIGHT_VAR = '--smart-app-banner-height'

function setHeightVar (value) {
  if (typeof document === 'undefined') return
  document.documentElement.style.setProperty(HEIGHT_VAR, value)
}

// Hidden (v-if removed) and display:none (>= sm) both measure 0.
function syncHeightVar () {
  const height = bannerRef.value?.getBoundingClientRect().height ?? 0
  setHeightVar(`${Math.round(height)}px`)
}

// Banner mounts/unmounts via v-if, so wait a tick for the DOM before measuring.
watch(showBanner, () => {
  nextTick(syncHeightVar)
})

const isIos = computed(() => {
  if (typeof navigator === 'undefined') return false
  return /(iPad|iPhone|iPod)/i.test(navigator.userAgent)
})

const isSafari = computed(() => {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return /Safari/i.test(ua) && !/CriOS|FxiOS|OPiOS|Chrome|Chromium/i.test(ua)
})

const isAndroid = computed(() => {
  if (typeof navigator === 'undefined') return false
  return /Android/i.test(navigator.userAgent)
})

const storeUrl = computed(() => {
  return isIos.value
    ? memberhubConstants.IOS_APP_STORE_URL
    : memberhubConstants.ANDROID_APP_STORE_URL
})

const isDashboardRoute = computed(() => {
  return route.path.startsWith('/dashboard')
})

function shouldShow () {
  // Don't show inside the native Capacitor app
  if (Capacitor.isNativePlatform() || isTWA()) return false

  // Only on dashboard routes
  if (!isDashboardRoute.value) return false

  // iOS Safari uses native meta tag — skip custom banner
  if (isIos.value && isSafari.value) return false

  // Only show on mobile devices (iOS non-Safari or Android)
  if (!isIos.value && !isAndroid.value) return false

  // Check dismiss expiry
  const dismissedAt = localStorage.getItem(DISMISS_KEY)
  if (dismissedAt) {
    const daysSince = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24)
    if (daysSince < DISMISS_DAYS) return false
  }

  return true
}

function dismiss () {
  localStorage.setItem(DISMISS_KEY, String(Date.now()))
  showBanner.value = false
  // Clear the offset in the same tick as the dismiss. The watcher above also
  // re-measures on nextTick, but writing it here means consumers can never be
  // left holding a stale offset (a white gap where the banner used to be) if
  // the re-measure is delayed or a re-render never happens.
  setHeightVar('0px')
  // Flush layout in the same frame as the dismiss. Reading a layout property is
  // enough to force the recalc, so the header/sidebar can't be left painted at
  // the old offset until the next reload on mobile WebKit.
  void document.documentElement.offsetHeight
}

watch(isDashboardRoute, () => {
  showBanner.value = shouldShow()
})

onMounted(() => {
  showBanner.value = shouldShow()
  nextTick(syncHeightVar)
  // resize covers crossing the sm breakpoint (where the banner is display:none);
  // pageshow covers a back/forward-cache restore, which replays the DOM without
  // re-running mount.
  window.addEventListener('resize', syncHeightVar, { passive: true })
  window.addEventListener('pageshow', syncHeightVar)
})

onUnmounted(() => {
  window.removeEventListener('resize', syncHeightVar)
  window.removeEventListener('pageshow', syncHeightVar)
  if (typeof document === 'undefined') return
  document.documentElement.style.removeProperty(HEIGHT_VAR)
})
</script>

